import { Grid, Typography, Stack, Chip, alpha, Box } from "@mui/material";
import { Profile } from '../ScreeningProfiles/types';
import { Document } from './types';
import { checkProfileCompleteness } from "../../utils/profileUtils";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

interface ProfileInfoProps {
  profile: Profile;
  documents: Document[];
  onDocumentClick?: (document: Document) => void;
}

const ProfileInfo = ({ profile, documents, onDocumentClick }: ProfileInfoProps) => {
  const validationResult = checkProfileCompleteness(profile, documents);
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const formatDate = (date: string) => {
    return moment(date).locale(i18n.language).format('LL');
  };

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const renderName = () => {
    const nameParts = [
      profile.firstname,
      profile.initials,
      profile.surname
    ].filter(Boolean);
    
    if (nameParts.length === 0) return null;
    
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary' }}>
          {t('profile.name')}
        </Typography>
        <Typography variant="body2" >{nameParts.join(' ')}</Typography>
      </Stack>
    );
  };

  const renderBirthInfo = () => {
    const birthParts = [
      profile.birthdate && formatDate(profile.birthdate) + ", ",
      profile.birthplace && profile.birthplace
    ].filter(Boolean);
    
    if (birthParts.length === 0) return null;
    
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary' }}>
          {t('profile.dateofbirth')}
        </Typography>
        <Typography variant="body2" >{birthParts.join(' ')}</Typography>
      </Stack>
    );
  };

  const renderContact = () => {
    const contactParts = [
      profile.email,
      profile.phoneNumber
    ].filter(Boolean);
    
    if (contactParts.length === 0) return null;
    
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary' }}>
          {t('profile.contact')}
        </Typography>
        <Typography variant="body2" >{contactParts.join(' --- ')}</Typography>
      </Stack>
    );
  };

  const renderAddress = () => {
    const addressParts = [
      profile.street,
      profile.houseNumber,
      profile.postalCode,
      profile.city
    ].filter(Boolean);
    
    if (addressParts.length === 0) return null;
    
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary' }}>
          {t('profile.address')}
        </Typography>
        <Typography variant="body2" >{addressParts.join(' ')}</Typography>
      </Stack>
    );
  };

  const renderLocation = () => {
    const locationParts = [
      profile.province,
      profile.country
    ].filter(Boolean);
    
    if (locationParts.length === 0) return null;
    
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary' }}>
          {t('profile.province')}
        </Typography>
        <Typography variant="body2" >{locationParts.join(', ')}</Typography>
      </Stack>
    );
  };

  const renderScreeningInfo = () => {
    if (!profile.screeningType && !profile.employmentType && !profile.income) return null;

    return (
      <>
        <Stack direction="row" spacing={2} alignItems="center">
          {profile.income && (
            <>
              <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary' }}>
                {t('screening_profile.income')}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Typography variant="body2">
                  {formatMoney(profile.income)} {t('screening_profile.per_month')}
                </Typography>
              </Stack>
            </>
          )}
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary' }}>
            {t('screening_profile.screening_type')}
          </Typography>
          <Stack direction="row" spacing={1}>
            {profile.screeningType && (
              <Chip 
                label={t(`screening_profile.${profile.screeningType.toLowerCase()}`)} 
                size="small" 
                sx={{ 
                  backgroundColor: alpha('#1976d2', 0.1),
                  color: 'primary.main',
                  fontWeight: 500
                }}
              />
            )}
            {profile.employmentType && (
              <Chip 
                label={t(`screening_profile.${profile.employmentType.toLowerCase()}`)} 
                size="small" 
                sx={{ 
                  backgroundColor: alpha('#9c27b0', 0.1),
                  color: 'secondary.main',
                  fontWeight: 500
                }}
              />
            )}
          </Stack>
        </Stack>
      </>
    );
  };

  const renderMissingInfo = () => {
    if (validationResult.missingFields.length === 0 && validationResult.missingDocuments.length === 0) {
      return null;
    }

    const allMissingItems = [
      ...validationResult.missingFields.map(field => t(field)),
      ...validationResult.missingDocuments.map(doc => t(doc))
    ];

    return (
      <Box sx={{ 
        backgroundColor: 'red',
        color: 'white',
        p: 2,
        borderRadius: 1,
        margin: "0 -2rem",
        mt: "2rem",
      }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ErrorOutlineIcon sx={{ color: 'white' }} fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {t('screening_profile.missing_information')}:
          </Typography>
          <Typography variant="body2">
            {allMissingItems.join(', ')}
          </Typography>
        </Stack>
      </Box>
    );
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Stack spacing={2}>
            {renderName()}
            {renderBirthInfo()}
            {renderContact()}
            {renderAddress()}
            {renderLocation()}
            {renderScreeningInfo()}
            {documents.length > 0 && (
              <Stack direction="row" gap={1} alignItems="center">
                <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary' }}>
                  {t('screening_profile.documents')}
                </Typography>
                <Stack direction="row" gap={2} flexWrap="wrap">
                  {documents.map((doc) => (
                    <Chip
                      key={doc.id}
                      label={t(`screening_profile.doc_${doc.screeningDocumentType.toLowerCase()}`)}
                      size="small"
                      clickable
                      onClick={() => onDocumentClick?.(doc)}
                      sx={{
                        borderRadius: '4px',
                        backgroundColor: theme.palette.secondary.main,
                        color: 'info.main',
                        padding: '0.2rem 0.5rem',
                        margin: 0,
                        '&:hover': {
                          backgroundColor: theme.palette.secondary.main,
                          boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.1)'
                        }
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            )}
          </Stack>
        </Grid>
      </Grid>
      {renderMissingInfo()}
    </>
  );
};

export default ProfileInfo; 